import type { i18n as I18nInstance, TFunction } from "i18next";
import { polishCountableForm } from "@/lib/i18n/polishCountableForm";

type I18nForCountPhrase = Pick<I18nInstance, "exists" | "language">;

/**
 * Non-negative integer for plural rules and `{{count}}` interpolation.
 * Negative and non-finite values are treated as 0 so copy stays predictable.
 */
export function normalizeCountForPhrase(count: number): number {
  if (!Number.isFinite(count)) {
    return 0;
  }
  const truncated = Math.trunc(count);
  return truncated < 0 ? 0 : truncated;
}

/**
 * For some locales, picks an explicit suffix (`stem_one` / `_few` / `_many`);
 * `undefined` → use i18next **`t(stem, { count })`** plural resolution.
 */
export function countPhraseFormForLocale(
  language: string,
  count: number,
): string | undefined {
  if (language.startsWith("pl")) {
    return polishCountableForm(count);
  }
  return undefined;
}

/**
 * Resolves localized copy for a numeric **count** and translation **stem** (base key).
 * Polish prefers **`stem_${form}`** when that key exists; otherwise falls back to
 * **`t(stem, { count })`** (i18next plural rules + `*_other` etc.).
 */
export function resolveCountPhrase(
  t: TFunction,
  i18n: I18nForCountPhrase,
  count: number,
  stem: string,
): string {
  const safeCount = normalizeCountForPhrase(count);
  const lang = i18n.language ?? "";
  const form = countPhraseFormForLocale(lang, safeCount);
  if (form !== undefined) {
    const candidate = `${stem}_${form}`;
    if (i18n.exists(candidate)) {
      return t(candidate, { count: safeCount });
    }
  }
  return t(stem, { count: safeCount });
}
